import { ref, nextTick } from "vue";
import { FetchResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { routerPath } from "@/common/router";
import { REGULAR_DELIVERY_LIST_FILE_NAME } from "@/views/RegularDeliveryList/constants";
import regularDeliveryStore from "@/store/regularDeliveryStore";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import {
  CreateRegularDeliveryDocument,
  CreateRegularDeliveryMutation,
  CreateRegularDeliveryMutationVariables,
  UpdateRegularDeliveryDocument,
  UpdateRegularDeliveryMutation,
  UpdateRegularDeliveryMutationVariables,
  DeleteRegularDeliveryDocument,
  DeleteRegularDeliveryMutation,
  DeleteRegularDeliveryMutationVariables,
  CreateMemberDocument,
  CreateMemberMutation,
  CreateMemberMutationVariables,
  UpdateMemberDocument,
  UpdateMemberMutation,
  UpdateMemberMutationVariables,
} from "@/generated/graphql";

/**
 * 定期配送登録画面の確認/登録/完了アクションを扱う composable。
 *
 * @param ctx 画面側で保持している状態・入力値・Mutation レスポンス参照をまとめたコンテキスト
 * @returns 画面の各ボタンから呼び出すイベントハンドラ群と Mutation レスポンス参照
 */
export const useRegularDeliveryRegisterSubmit = (ctx: Record<string, any>) => {
  const {
    displayConfirmDialog,
    displayCompletedDialog,
    router,
    deleteMode,
    overlay,
    deliveryIdSelectValues,
    regularDeliveryCreateInput,
    deliverySelectValues,
    deliveryRequestCreateTimeValues,
    updateMode,
    dummy_payment_amount,
    regularDeliveryUpdateInput,
    operationInput,
    errdialog,
    errMessage,
    memberCreateInput,
    memberUpdateInput,
    createMemberMutation,
    updateMemberMutation,
    deliveryRequestCreateDateValues,
  } = ctx;

/**
 * 確認ダイアログを開く。
 *
 * @returns Promise<void> ダイアログ表示状態の更新完了を示す
 */
const onClickOpenConfirmDialog = async (): Promise<void> => {
  // 店舗登録確認ダイアログ 表示
  displayConfirmDialog.value = true;
};

/**
 * 入力を破棄して一覧画面へ戻る。
 *
 * @returns Promise<void> 画面遷移完了を示す
 */
const onClickInputCancel = async (): Promise<void> => {
  nextTick(async () => {
    //入力項目をクリア
    allClear();
    // 「定期配送登録」画面へ遷移
    await router.push({ path: routerPath.regularDeliveryList });
  });
};

/**
 * 確認ダイアログを閉じて一覧へ戻る。
 *
 * @returns Promise<void> 画面遷移完了を示す
 */
const onClickBack = async (): Promise<void> => {
  // 定期配送登録確認ダイアログ 非表示
  displayConfirmDialog.value = false;
  await router.push({
    name: REGULAR_DELIVERY_LIST_FILE_NAME,
    query: { searchMode: "true" },
  });
};

////////////////////////////////////////////////////////////////////////////////
//  確認画面
////////////////////////////////////////////////////////////////////////////////
// 定期配送登録 レスポンス
const createRegularDeliveryMutation = ref<CreateRegularDeliveryMutation | null | undefined>(null);

// 定期配送変更 レスポンス
const updateRegularDeliveryMutation = ref<UpdateRegularDeliveryMutation | null | undefined>(null);

// 定期配送削除 レスポンス
const deleteRegularDeliveryMutation = ref<DeleteRegularDeliveryMutation | null | undefined>(null);

/**
 * 配送曜日の選択値から GraphQL 入力用の曜日フラグを組み立てる。
 *
 * @returns Promise<void> regularDeliveryCreateInput への反映完了を示す
 */
const createDeliveryId = async (): Promise<void> => {
  // 未選択曜日が前回入力のまま残らないよう、毎回 0 で初期化する。
  regularDeliveryCreateInput.mon_to_deliver = 0;
  regularDeliveryCreateInput.tue_to_deliver = 0;
  regularDeliveryCreateInput.wed_to_deliver = 0;
  regularDeliveryCreateInput.thu_to_deliver = 0;
  regularDeliveryCreateInput.fri_to_deliver = 0;
  regularDeliveryCreateInput.sat_to_deliver = 0;
  regularDeliveryCreateInput.sun_to_deliver = 0;

  // 画面の選択値は「1〜7」の曜日コードで管理しており、API には曜日別フラグで渡す。
  // 1:月, 2:火, 3:水, 4:木, 5:金, 6:土, 7:日
  if (
    deliveryIdSelectValues.value !== undefined &&
    deliveryIdSelectValues.value !== null &&
    deliveryIdSelectValues.value.length > 0
  ) {
    for (let deliveryIdkey of deliveryIdSelectValues.value) {
      switch (String(deliveryIdkey.value)) {
        case "1":
          regularDeliveryCreateInput.mon_to_deliver = 1;
          break;
        case "2":
          regularDeliveryCreateInput.tue_to_deliver = 1;
          break;
        case "3":
          regularDeliveryCreateInput.wed_to_deliver = 1;
          break;
        case "4":
          regularDeliveryCreateInput.thu_to_deliver = 1;
          break;
        case "5":
          regularDeliveryCreateInput.fri_to_deliver = 1;
          break;
        case "6":
          regularDeliveryCreateInput.sat_to_deliver = 1;
          break;
        case "7":
          regularDeliveryCreateInput.sun_to_deliver = 1;
          break;
      }
    }
  }
};

/**
 * 確認画面の実行ボタンから、会員情報と定期配送情報を登録/更新する。
 *
 * @returns Promise<void> 正常時は一覧画面へ遷移し、失敗時はエラーダイアログ表示で処理終了
 */
const onClickConfirmEntry = async (): Promise<void> => {
  overlay.value = true;

  try {
    // 画面選択の曜日値を API 入力形式に正規化する。
    await createDeliveryId();

    // 登録内容設定: 画面の選択値を GraphQL 入力型へ詰め替える。
    // エリアコード
    regularDeliveryCreateInput.area_code = deliverySelectValues.value
      ? deliverySelectValues.value?.area_code
      : "";
    // 便ID
    regularDeliveryCreateInput.delivery_id = deliverySelectValues.value
      ? Number(deliverySelectValues.value?.delivery_id)
      : 0;
    // 配達依頼作成時間
    regularDeliveryCreateInput.delivery_request_create_time =
      String(deliveryRequestCreateTimeValues.value).padStart(2, "0") +
      ":00:00";
    // 空文字を undefined に寄せることで、バックエンド側の「未指定」判定と合わせる。
    // 定期配達有効期間（開始）
    if (
      regularDeliveryCreateInput.valid_date_from == null ||
      regularDeliveryCreateInput.valid_date_from == ""
    ) {
      regularDeliveryCreateInput.valid_date_from = undefined;
    }
    // 定期配達有効期間（終了）
    if (
      regularDeliveryCreateInput.valid_date_to == null ||
      regularDeliveryCreateInput.valid_date_to == ""
    ) {
      regularDeliveryCreateInput.valid_date_to = undefined;
    }
    // 配達備考
    if (
      regularDeliveryCreateInput.delivery_notes == null ||
      regularDeliveryCreateInput.delivery_notes == ""
    ) {
      regularDeliveryCreateInput.delivery_notes = undefined;
    }
    // 業務要件: 定期配送の登録前に配送先会員を必ず最新化する。
    if (!regularDeliveryStore.user_id) {
      await createMember();
    } else {
      await updateMember();
    }
    if (!updateMode.value) {
      // 金額は画面表示用にカンマ区切り文字列のため、登録時に数値化する。
      const removed = dummy_payment_amount.value.replace(/,/g, "");
      regularDeliveryCreateInput.payment_amount = parseInt(removed, 10);
      // 定期配送作成
      if (!(await createRegularDelivery())) {
        return;
      }
      // 定期配送登録確認ダイアログ 非表示
      displayConfirmDialog.value = false;

      // 編集/削除と同様に一覧へ戻る
      await router.push({
        name: REGULAR_DELIVERY_LIST_FILE_NAME,
        query: { searchMode: "true" },
      });
      overlay.value = false;
    } else {
      // 変更 API の入力型へコピーしてから更新する。
      await copyCreateToUpdate();

      // 定期配送変更
      if (!(await updateRegularDelivery())) {
        return;
      }

      // 一覧に戻る
      await router.push({
        name: REGULAR_DELIVERY_LIST_FILE_NAME,
        query: { searchMode: "true" },
      });
    }
  } finally {
    overlay.value = false;
  }
};

/**
 * 確認画面から定期配送を削除する。
 *
 * @returns Promise<void> 正常時は一覧へ遷移、失敗時はその場で終了
 */
const onClickDelete = async (): Promise<void> => {
  // 定期配送変更
  if (!(await deleteRegularDelivery())) {
    return;
  }

  // 一覧に戻る
  await router.push({
    name: REGULAR_DELIVERY_LIST_FILE_NAME,
    query: { searchMode: "true" },
  });
};

/**
 * 確認ダイアログのキャンセル処理を行う。
 *
 * @returns Promise<void> 削除モード時は一覧遷移、それ以外はダイアログを閉じる
 */
const onClickConfirmCancel = async (): Promise<void> => {
  if (deleteMode.value) {
    // 一覧に戻る
    await router.push({
      name: REGULAR_DELIVERY_LIST_FILE_NAME,
      query: { searchMode: "true" },
    });
  } else {
    // 定期配送登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
  }
};

/**
 * 定期配送を新規作成する GraphQL Mutation を実行する。
 *
 * @returns Promise<boolean> `true`: 成功, `false`: エラー表示済みで失敗
 */
const createRegularDelivery = async (): Promise<boolean> => {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 定期配送作成 logInput
    operationInput.detail = "定期配送登録";
    operationInput.display_category = "定期配送登録";
    const result: FetchResult<CreateRegularDeliveryMutation> =
      await apolloClient.mutate<
        CreateRegularDeliveryMutation,
        CreateRegularDeliveryMutationVariables
      >({
        mutation: CreateRegularDeliveryDocument,
        variables: {
          input: regularDeliveryCreateInput,
          logInput: operationInput,
        },
      });
    createRegularDeliveryMutation.value = result.data;
    if (
      createRegularDeliveryMutation.value === undefined ||
      createRegularDeliveryMutation.value === null ||
      !createRegularDeliveryMutation.value.createRegularDelivery?.Result
    ) {
      errdialog.value = true;
      errMessage.value = "定期配送の登録に失敗しました。";
      return false;
    }

    return true;
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }

    errdialog.value = true;
    errMessage.value = "定期配送の登録に失敗しました。";
    return false;
  }
};

/**
 * 定期配送を更新する GraphQL Mutation を実行する。
 *
 * @returns Promise<boolean> `true`: 成功, `false`: エラー表示済みで失敗
 */
const updateRegularDelivery = async (): Promise<boolean> => {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 定期配送作成 logInput
    operationInput.detail = "定期配送変更";
    operationInput.display_category = "定期配送変更";

    const result: FetchResult<UpdateRegularDeliveryMutation> =
      await apolloClient.mutate<
        UpdateRegularDeliveryMutation,
        UpdateRegularDeliveryMutationVariables
      >({
        mutation: UpdateRegularDeliveryDocument,
        variables: {
          input: regularDeliveryUpdateInput,
          logInput: operationInput,
        },
      });
    updateRegularDeliveryMutation.value = result.data;

    if (
      updateRegularDeliveryMutation.value === undefined ||
      updateRegularDeliveryMutation.value === null ||
      !updateRegularDeliveryMutation.value.updateRegularDelivery?.Result
    ) {
      errdialog.value = true;
      errMessage.value = "定期配送の変更に失敗しました。";
      return false;
    }

    return true;
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }

    errdialog.value = true;
    errMessage.value = "定期配送の変更に失敗しました。";
    return false;
  }
};

/**
 * 定期配送を削除する GraphQL Mutation を実行する。
 *
 * @returns Promise<boolean> `true`: 成功, `false`: エラー表示済みで失敗
 */
const deleteRegularDelivery = async (): Promise<boolean> => {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 定期配送作成 logInput
    operationInput.detail = "定期配送削除";
    operationInput.display_category = "定期配送削除";
    const result: FetchResult<DeleteRegularDeliveryMutation> =
      await apolloClient.mutate<
        DeleteRegularDeliveryMutation,
        DeleteRegularDeliveryMutationVariables
      >({
        mutation: DeleteRegularDeliveryDocument,
        variables: {
          regular_delivery_no:
            regularDeliveryUpdateInput.regular_delivery_no,
          logInput: operationInput,
        },
      });
    deleteRegularDeliveryMutation.value = result.data;

    if (
      deleteRegularDeliveryMutation.value === undefined ||
      deleteRegularDeliveryMutation.value === null ||
      !deleteRegularDeliveryMutation.value.deleteRegularDelivery?.Result
    ) {
      errdialog.value = true;
      errMessage.value = "定期配送の削除に失敗しました。";
      return false;
    }

    return true;
  } catch (err: unknown) {
    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }

    errdialog.value = true;
    errMessage.value = "定期配送の削除に失敗しました。";
    return false;
  }
};

/**
 * 新規入力用オブジェクトを更新用オブジェクトへコピーする。
 *
 * @returns Promise<void> regularDeliveryUpdateInput への反映完了を示す
 */
const copyCreateToUpdate = async (): Promise<void> => {
  // TODO: create/update の入力構造がほぼ同一のため、共通マッパー関数化で保守性を上げる余地がある。
  regularDeliveryUpdateInput.chain_code =
    regularDeliveryCreateInput.chain_code;
  regularDeliveryUpdateInput.shop_code =
    regularDeliveryCreateInput.shop_code;
  regularDeliveryUpdateInput.area_code =
    regularDeliveryCreateInput.area_code;
  regularDeliveryUpdateInput.delivery_id =
    regularDeliveryCreateInput.delivery_id;
  regularDeliveryUpdateInput.irregular_delivery_start_time =
    regularDeliveryCreateInput.irregular_delivery_start_time;
  regularDeliveryUpdateInput.irregular_delivery_end_time =
    regularDeliveryCreateInput.irregular_delivery_end_time;
  regularDeliveryUpdateInput.delivery_request_create_date =
    regularDeliveryCreateInput.delivery_request_create_date;
  regularDeliveryUpdateInput.delivery_request_create_time =
    regularDeliveryCreateInput.delivery_request_create_time;
  regularDeliveryUpdateInput.mon_to_deliver =
    regularDeliveryCreateInput.mon_to_deliver;
  regularDeliveryUpdateInput.tue_to_deliver =
    regularDeliveryCreateInput.tue_to_deliver;
  regularDeliveryUpdateInput.wed_to_deliver =
    regularDeliveryCreateInput.wed_to_deliver;
  regularDeliveryUpdateInput.thu_to_deliver =
    regularDeliveryCreateInput.thu_to_deliver;
  regularDeliveryUpdateInput.fri_to_deliver =
    regularDeliveryCreateInput.fri_to_deliver;
  regularDeliveryUpdateInput.sat_to_deliver =
    regularDeliveryCreateInput.sat_to_deliver;
  regularDeliveryUpdateInput.sun_to_deliver =
    regularDeliveryCreateInput.sun_to_deliver;
  regularDeliveryUpdateInput.valid_date_from =
    regularDeliveryCreateInput.valid_date_from;
  regularDeliveryUpdateInput.valid_date_to =
    regularDeliveryCreateInput.valid_date_to;
  regularDeliveryUpdateInput.member_id =
    regularDeliveryCreateInput.member_id;
  regularDeliveryUpdateInput.delivery_notes =
    regularDeliveryCreateInput.delivery_notes;
  regularDeliveryUpdateInput.delivery_address1 =
    regularDeliveryCreateInput.delivery_address1;
  regularDeliveryUpdateInput.delivery_address2 =
    regularDeliveryCreateInput.delivery_address2;
  regularDeliveryUpdateInput.delivery_address3 =
    regularDeliveryCreateInput.delivery_address3;
  regularDeliveryUpdateInput.delivery_name1 =
    regularDeliveryCreateInput.delivery_name1;
  regularDeliveryUpdateInput.delivery_name2 =
    regularDeliveryCreateInput.delivery_name2;
  regularDeliveryUpdateInput.delivery_name_kana1 =
    regularDeliveryCreateInput.delivery_name_kana1;
  regularDeliveryUpdateInput.delivery_name_kana2 =
    regularDeliveryCreateInput.delivery_name_kana2;
  regularDeliveryUpdateInput.delivery_zip_code =
    regularDeliveryCreateInput.delivery_zip_code;
  regularDeliveryUpdateInput.delivery_phone =
    regularDeliveryCreateInput.delivery_phone;
  regularDeliveryUpdateInput.member_notes =
    regularDeliveryCreateInput.member_notes;
  // 金額は表示フォーマットを除去して数値として更新 API に渡す。
  const removed = dummy_payment_amount.value.replace(/,/g, "");
  regularDeliveryUpdateInput.payment_amount = parseInt(removed, 10);
  regularDeliveryUpdateInput.payment_method =
    regularDeliveryCreateInput.payment_method;
  regularDeliveryUpdateInput.unit = regularDeliveryCreateInput.unit;
  regularDeliveryUpdateInput.house_card =
    regularDeliveryCreateInput.house_card;
};

/**
 * 配送先入力をもとに会員情報を新規作成する。
 *
 * @returns Promise<void> 成功時は作成した会員IDを regularDeliveryCreateInput.member_id へ格納
 */
const createMember = async (): Promise<void> => {
  try {
    // 会員マスタと定期配送入力の二重管理を避けるため、配送先入力をそのまま会員入力へ転記する。
    memberCreateInput.address1 =
      regularDeliveryCreateInput.delivery_address1;
    memberCreateInput.address2 =
      regularDeliveryCreateInput.delivery_address2;
    memberCreateInput.address3 =
      regularDeliveryCreateInput.delivery_address3;
    memberCreateInput.chain_code =
      regularDeliveryCreateInput.chain_code;
    memberCreateInput.customer_code = localStorageGetCustomerCode();
    memberCreateInput.name1 =
      regularDeliveryCreateInput.delivery_name1;
    memberCreateInput.name2 =
      regularDeliveryCreateInput.delivery_name2;
    memberCreateInput.name_kana1 =
      regularDeliveryCreateInput.delivery_name_kana1;
    memberCreateInput.name_kana2 =
      regularDeliveryCreateInput.delivery_name_kana2;
    memberCreateInput.notes =
      regularDeliveryCreateInput.member_notes;
    memberCreateInput.phone =
      regularDeliveryCreateInput.delivery_phone;
    memberCreateInput.shop_code =
      regularDeliveryCreateInput.shop_code;
    memberCreateInput.zip_code =
      regularDeliveryCreateInput.delivery_zip_code;
    memberCreateInput.house_card =
      regularDeliveryCreateInput.house_card;

    // 定期配送作成 logInput
    operationInput.detail = "会員登録";
    operationInput.display_category = "会員登録";

    //graphql接続
    const apolloClient = await generateApolloClient();

    const result: FetchResult<CreateMemberMutation> =
      await apolloClient.mutate<
        CreateMemberMutation,
        CreateMemberMutationVariables
      >({
        mutation: CreateMemberDocument,
        variables: {
          input: memberCreateInput,
          logInput: operationInput,
        },
      });

    createMemberMutation.value = result.data;
    if (
      createMemberMutation.value !== null &&
      createMemberMutation.value !== undefined
    ) {
      regularDeliveryCreateInput.member_id = createMemberMutation.value
        ?.createMember?.member_id
        ? Number(createMemberMutation.value?.createMember?.member_id)
        : 0;
    }
  } catch (err: unknown) {
    console.log("err_会員情報を登録する");

    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }

    errdialog.value = true;
    errMessage.value = "会員情報の登録に失敗しました。";
    return;
  }
};

/**
 * 既存会員情報を配送先入力で更新する。
 *
 * @returns Promise<void> 成功時は regularDeliveryCreateInput.member_id を既存会員IDで確定
 */
const updateMember = async (): Promise<void> => {
  try {
    //  会員更新 input
    memberUpdateInput.customer_code = localStorageGetCustomerCode();
    memberUpdateInput.chain_code =
      regularDeliveryCreateInput.chain_code;
    memberUpdateInput.address1 =
      regularDeliveryCreateInput.delivery_address1;
    memberUpdateInput.address2 =
      regularDeliveryCreateInput.delivery_address2;
    memberUpdateInput.address3 =
      regularDeliveryCreateInput.delivery_address3;
    memberUpdateInput.member_id = regularDeliveryStore.user_id;
    memberUpdateInput.name1 =
      regularDeliveryCreateInput.delivery_name1;
    memberUpdateInput.name2 =
      regularDeliveryCreateInput.delivery_name2;
    memberUpdateInput.name_kana1 =
      regularDeliveryCreateInput.delivery_name_kana1;
    memberUpdateInput.name_kana2 =
      regularDeliveryCreateInput.delivery_name_kana2;
    // 検索用かなは姓・名を連結した値を保持する運用。
    memberUpdateInput.search_kana =
      regularDeliveryCreateInput.delivery_name_kana1 +
      regularDeliveryCreateInput.delivery_name_kana2;
    memberUpdateInput.notes =
      regularDeliveryCreateInput.member_notes;
    memberUpdateInput.phone =
      regularDeliveryCreateInput.delivery_phone;
    memberUpdateInput.shop_code =
      regularDeliveryCreateInput.shop_code;
    memberUpdateInput.zip_code =
      regularDeliveryCreateInput.delivery_zip_code;
    memberUpdateInput.house_card =
      regularDeliveryCreateInput.house_card!;

    //graphql接続
    const apolloClient = await generateApolloClient();

    const result: FetchResult<UpdateMemberMutation> =
      await apolloClient.mutate<
        UpdateMemberMutation,
        UpdateMemberMutationVariables
      >({
        mutation: UpdateMemberDocument,
        variables: {
          input: memberUpdateInput,
          logInput: operationInput,
        },
      });

    updateMemberMutation.value = result.data;
    if (
      updateMemberMutation.value !== null &&
      updateMemberMutation.value !== undefined
    ) {
      regularDeliveryCreateInput.member_id = Number(
        regularDeliveryStore.user_id
      );
    }
  } catch (err: unknown) {
    console.log("err_会員情報を更新する");

    if (err instanceof Error) {
      console.error(err);
    } else {
      console.error("未知のエラー:", err);
    }

    errdialog.value = true;
    errMessage.value = "会員情報の更新に失敗しました。";
    return;
  }
};

////////////////////////////////////////////////////////////////////////////////
//  完了画面
////////////////////////////////////////////////////////////////////////////////

/**
 * 完了ダイアログから一覧へ戻る。
 *
 * @returns Promise<void> 入力初期化と画面遷移完了を示す
 */
const onClickCompletedGoStart = async (): Promise<void> => {
  // 定期配送登録完了ダイアログ 非表示
  displayCompletedDialog.value = false;

  nextTick(async () => {
    //入力項目をクリア
    allClear();
    // 「定期配送登録」画面へ遷移
    await router.push({ path: routerPath.regularDeliveryList });
  });
};

/**
 * 画面内の入力状態を初期値へ戻す。
 *
 * @returns Promise<void> 入力状態リセット完了を示す
 */
const allClear = async (): Promise<void> => {
  // 登録用input
  regularDeliveryCreateInput.chain_code = "";
  regularDeliveryCreateInput.shop_code = "";
  regularDeliveryCreateInput.area_code = "";
  // 99 は「便未選択」を表す既定値として画面/API 間で利用している。
  regularDeliveryCreateInput.delivery_id = 99;
  regularDeliveryCreateInput.irregular_delivery_start_time = "";
  regularDeliveryCreateInput.irregular_delivery_end_time = "";
  regularDeliveryCreateInput.delivery_request_create_date = 0;
  regularDeliveryCreateInput.delivery_request_create_time = "";
  regularDeliveryCreateInput.mon_to_deliver = 0;
  regularDeliveryCreateInput.tue_to_deliver = 0;
  regularDeliveryCreateInput.wed_to_deliver = 0;
  regularDeliveryCreateInput.thu_to_deliver = 0;
  regularDeliveryCreateInput.fri_to_deliver = 0;
  regularDeliveryCreateInput.sat_to_deliver = 0;
  regularDeliveryCreateInput.sun_to_deliver = 0;
  regularDeliveryCreateInput.valid_date_from = undefined;
  regularDeliveryCreateInput.valid_date_to = undefined;
  regularDeliveryCreateInput.delivery_notes = undefined;
  regularDeliveryCreateInput.delivery_address1 = "";
  regularDeliveryCreateInput.delivery_address2 = "";
  regularDeliveryCreateInput.delivery_address3 = "";
  regularDeliveryCreateInput.delivery_name1 = "";
  regularDeliveryCreateInput.delivery_name2 = "";
  regularDeliveryCreateInput.delivery_name_kana1 = "";
  regularDeliveryCreateInput.delivery_name_kana2 = "";
  regularDeliveryCreateInput.delivery_phone = "";
  regularDeliveryCreateInput.delivery_zip_code = "";
  regularDeliveryCreateInput.member_notes = "";
  regularDeliveryCreateInput.payment_amount = null;
  regularDeliveryCreateInput.payment_method = "";
  regularDeliveryCreateInput.unit = 0;
  regularDeliveryCreateInput.house_card = "";

  // 変更用input
  regularDeliveryUpdateInput.regular_delivery_no = "";
  regularDeliveryUpdateInput.chain_code = "";
  regularDeliveryUpdateInput.shop_code = "";
  regularDeliveryUpdateInput.area_code = "";
  // 99 は「便未選択」を表す既定値として画面/API 間で利用している。
  regularDeliveryUpdateInput.delivery_id = 99;
  regularDeliveryUpdateInput.irregular_delivery_start_time = "";
  regularDeliveryUpdateInput.irregular_delivery_end_time = "";
  regularDeliveryUpdateInput.delivery_request_create_date = 0;
  regularDeliveryUpdateInput.delivery_request_create_time = "";
  regularDeliveryUpdateInput.mon_to_deliver = 0;
  regularDeliveryUpdateInput.tue_to_deliver = 0;
  regularDeliveryUpdateInput.wed_to_deliver = 0;
  regularDeliveryUpdateInput.thu_to_deliver = 0;
  regularDeliveryUpdateInput.fri_to_deliver = 0;
  regularDeliveryUpdateInput.sat_to_deliver = 0;
  regularDeliveryUpdateInput.sun_to_deliver = 0;
  regularDeliveryUpdateInput.valid_date_from = undefined;
  regularDeliveryUpdateInput.valid_date_to = undefined;
  regularDeliveryUpdateInput.delivery_notes = undefined;
  regularDeliveryUpdateInput.delivery_address1 = "";
  regularDeliveryUpdateInput.delivery_address2 = "";
  regularDeliveryUpdateInput.delivery_address3 = "";
  regularDeliveryUpdateInput.delivery_name1 = "";
  regularDeliveryUpdateInput.delivery_name2 = "";
  regularDeliveryUpdateInput.delivery_name_kana1 = "";
  regularDeliveryUpdateInput.delivery_name_kana2 = "";
  regularDeliveryUpdateInput.delivery_phone = "";
  regularDeliveryUpdateInput.delivery_zip_code = "";
  regularDeliveryUpdateInput.member_notes = "";
  regularDeliveryUpdateInput.payment_amount = null;
  regularDeliveryUpdateInput.payment_method = "";
  regularDeliveryUpdateInput.unit = 0;
  regularDeliveryUpdateInput.house_card = "";

  deliveryIdSelectValues.value = [];
  deliverySelectValues.value = null;
  deliveryRequestCreateDateValues.value = {
    // 0 は「当日作成」を示す業務コード。
    label: "当日",
    delivery_request_create_date: 0,
  };
}

  return {
    onClickOpenConfirmDialog,
    onClickInputCancel,
    onClickBack,
    createRegularDeliveryMutation,
    updateRegularDeliveryMutation,
    deleteRegularDeliveryMutation,
    createDeliveryId,
    onClickConfirmEntry,
    onClickDelete,
    onClickConfirmCancel,
    createRegularDelivery,
    updateRegularDelivery,
    deleteRegularDelivery,
    copyCreateToUpdate,
    createMember,
    updateMember,
    onClickCompletedGoStart,
    allClear,
  };
};
